import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCalcBlackList, useFilterCalcBlackLists } from "../../../../common/api/queries/calcBlackList";
import { TableSizes } from "../../../../common/constants";
import { formatAgentName } from "../../../agent/utils";
import { CalcTypeTag } from "../../records/components/CalcTypeTag";
import { CalcBlacklist, CalcBlacklistFilterPageRequest } from "../types";

interface Props {
  searchParams: CalcBlacklistFilterPageRequest;
  onUpdateClick: (calcBlacklist: CalcBlacklist) => void;
}

export const CalcBlacklistTable = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useFilterCalcBlackLists(searchParams);
  const deleteCalcBlackList = useDeleteCalcBlackList();

  const columns: ColumnsType<CalcBlacklist> = useMemo(
    () => [
      {
        key: "calcType",
        title: t("calc.enums.calcType._label"),
        width: 130,
        render: (_, record) => <CalcTypeTag type={record.calcType} style={{ marginRight: 0 }} />,
      },
      {
        key: "institutionId",
        title: t("calc.blacklist.attrs.institutionId"),
        width: 100,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
      },
      {
        key: "agentId",
        title: t("calc.blacklist.attrs.agentId"),
        width: 190,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAgentName(record.agent)}</Ellipsis>,
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("calc.blacklist.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => deleteCalcBlackList.mutate(record.id)}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      },
    ],
    [],
  );

  return (
    <Card className="card-box">
      <Table<CalcBlacklist>
        {...tableStandardProps()}
        columns={columns}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.SMALL }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
